import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { buildPrompt } from "./buildPrompt";

const AILightComponent = ({
  mode,
  param1,
  param2,
  audienceGender,
  audienceAge,
  audienceName
}) => {
  // console.log("Here on AILightComponent");
  const [selectedAction, setSelectedAction] = useState("AI Assistant");
  const [selectedLanguageCode, setSelectedLanguageCode] = useState("");

  const authState = useSelector((state) => state.authReducer);

  useEffect(() => {
    const userLang = authState?.user?.language;
    if (userLang) {
      const mapped = USER_LANG_TO_AI_LANG[userLang] || userLang;
      const isSupported = languageOptions.some((l) => l.code === mapped);
      setSelectedLanguageCode(isSupported ? mapped : "en");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount — dropdown stays fully editable after this

  const [wordLimit, setWordLimit] = useState(250); // characters
  const [outputFormat, setOutputFormat] = useState("Plain Unformatted Text");

  const [promptCreated, setPromptCreated] = useState(false);
  const [aiResponse, setAiResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [notesType, setNotesType] = useState("Quick Notes");

  const [selectedProvider, setSelectedProvider] = useState("openai");
  const apiKey = "sk-..."; // ← move to .env in real project!

  // const languageOptions = [
  //   { code: 'en', name: 'English' },
  //   { code: 'hi', name: 'Hindi' },
  //   { code: 'ar', name: 'Arabic' },
  //   { code: 'bn', name: 'Bengali' },
  //   { code: 'zh', name: 'Chinese' },
  //   { code: 'fr', name: 'French' },
  //   { code: 'de', name: 'German' },
  //   { code: 'gu', name: 'Gujarati' },
  //   { code: 'ja', name: 'Japanese' },
  //   { code: 'jv', name: 'Javanese' },
  //   { code: 'kn', name: 'Kannada' },
  //   { code: 'kok', name: 'Konkani' },
  //   { code: 'ko', name: 'Korean' },
  //   { code: 'ms', name: 'Malay' },
  //   { code: 'zh-CN', name: 'Mandarin' },
  //   { code: 'mr', name: 'Marathi' },
  //   { code: 'ne', name: 'Nepali' },
  //   { code: 'or', name: 'Oriya' },
  //   { code: 'ps', name: 'Pashto' },
  //   { code: 'pt-PT', name: 'Portuguese' },
  //   { code: 'pa', name: 'Punjabi' },
  //   { code: 'ru', name: 'Russian' },
  //   { code: 'sa', name: 'Sanskrit' },
  //   { code: 'si', name: 'Sinhala' },
  //   { code: 'ur', name: 'Urdu' },
  //   { code: 'vi', name: 'Vietnamese' },
  // ];

  const languageOptions = [
    { code: "en", name: "English" },
    { code: "hi", name: "Hindi" },
    { code: "ar", name: "Arabic" },
    { code: "as", name: "Assamese" },
    { code: "bn", name: "Bengali" },
    { code: "zh", name: "Chinese" },
    { code: "es", name: "Spanish" },
    { code: "fr", name: "French" },
    { code: "de", name: "German" },
    { code: "gu", name: "Gujarati" },
    { code: "id", name: "Indonesian" },
    { code: "ja", name: "Japanese" },
    { code: "jv", name: "Javanese" },
    { code: "kn", name: "Kannada" },
    { code: "kok", name: "Konkani" },
    { code: "ko", name: "Korean" },
    { code: "ml", name: "Malayalam" },
    { code: "ms", name: "Malay" },
    { code: "zh-CN", name: "Mandarin" },
    { code: "mr", name: "Marathi" },
    { code: "ne", name: "Nepali" },
    { code: "or", name: "Oriya" },
    { code: "ps", name: "Pashto" },
    { code: "pt-PT", name: "Portuguese" },
    { code: "pa", name: "Punjabi" },
    { code: "ru", name: "Russian" },
    { code: "sa", name: "Sanskrit" },
    { code: "si", name: "Sinhala" },
    { code: "ta", name: "Tamil" },
    { code: "te", name: "Telugu" },
    { code: "tr", name: "Turkish" },
    { code: "ur", name: "Urdu" },
    { code: "vi", name: "Vietnamese" },
  ];

  // Maps authState.user.language codes -> this component's languageOptions codes
  // Only entries where the code actually differs need listing; everything else
  // falls through to the identity default below.
  const USER_LANG_TO_AI_LANG = {
    zh: "zh-CN",
    pt: "pt-PT",
  };

  const actionGroups = [
    {
      label: "Language Transformation",
      options: ["Rewrite", "Restructure", "Summarise", "Translate", "Copyedit"],
    },

    {
      label: "Understanding & Clarity",
      options: [
        "Explain",
        "Details",
        "Provide More Information",
        "Provide Answer",
        "Provide Intent",
        "Make Note",
      ],
    },

    {
      label: "Analysis & Reasoning",
      options: [
        "Evaluate",
        "Check Accuracy",
        "Check Correctness",
        "Check Relevance",
        "Provide Sentiment",
        "Give Reasons",
        "Give Root Cause",
        "Give General Analysis",
        "Give Impact Analysis",
      ],
    },

    {
      label: "Action & Solution",
      options: [
        "Propose Solution",
        "Give Process Steps",
        "Give Countermeasures",
        "Complete It",
      ],
    },

    {
      label: "Ideation & Creativity",
      options: [
        "Give Idea",
        "Tips",
        "Give Questions",
        "Give Conversation or Dialogue",
        "Give Motivational Message",
      ],
    },

    {
      label: "Interaction Modes",
      options: ["Respond", "React"],
    },

    {
      label: "Governance Intelligence (Anugya AI)",
      options: [
        "Classify Intent",
        "Risk Analysis",
        "Compliance Check",
        "Consent Analysis",
        "Audit Readiness",
        "Governance Evaluation",
      ],
    },
  ];

  const formats = [
    "Plain Unformatted Text",
    "Plain Paragraphed Text",
    "Complete HTML Page",
    "HTML Div Only",
    "JSON Data",
    "Small SVG Image",
    "Emoji",
  ];

  const aiProviders = [
    {
      id: "openai",
      label: "ChatGPT (OpenAI)",
      shortName: "ChatGPT",
      color: "from-emerald-600 to-teal-600",
    },
    {
      id: "claude",
      label: "Claude (Anthropic)",
      shortName: "Claude",
      color: "from-orange-600 to-amber-600",
    },
    {
      id: "gemini",
      label: "Gemini (Google)",
      shortName: "Gemini",
      color: "from-blue-600 to-cyan-600",
    },
    {
      id: "grok",
      label: "Grok (xAI)",
      shortName: "Grok",
      color: "from-purple-600 to-violet-600",
    },
    {
      id: "mistral",
      label: "Mistral",
      shortName: "Mistral",
      color: "from-pink-600 to-rose-600",
    },
    {
      id: "deepseek",
      label: "DeepSeek",
      shortName: "DeepSeek",
      color: "from-cyan-600 to-teal-700",
    },
    {
      id: "perplexity",
      label: "Perplexity",
      shortName: "Perplexity",
      color: "from-indigo-600 to-purple-600",
    },
    {
      id: "think5whys",
      label: "Think5Whys",
      shortName: "Think5Whys",
      color: "from-teal-600 to-red-600",
    },
  ];

  const getProviderChatUrl = (providerId) => {
    const urls = {
      openai: "https://chatgpt.com/",
      claude: "https://claude.ai/chats",
      gemini: "https://gemini.google.com/app",
      grok: "https://grok.x.ai/",
      mistral: "https://chat.mistral.ai/chat",
      deepseek: "https://chat.deepseek.com/",
      perplexity: "https://www.perplexity.ai/",
      think5whys: "https://www.thinkwhys.ai/",
    };

    const url = urls[providerId];

    if (!url) {
      console.warn(`No chat URL defined for provider: ${providerId}`);
      return "https://chatgpt.com/";
    }

    return url;
  };

  const selectedLanguageName =
    languageOptions.find((l) => l.code === selectedLanguageCode)?.name || "";
  let rcaRuleBlock = "";

  if (wordLimit <= 180) {
    rcaRuleBlock = `
      Rules:
      - Respond strictly in ${selectedLanguageName || "English"}
      - Use structured format:
        1. Summary
        2. Key Issues
        3. Insights
        4. Recommendations
        5. Next Steps
      - Each section must be 1 short sentence only
      - Focus only on most critical points
      - No markdown
      - No bullets outside structure
      - Keep response extremely concise and action oriented
      `;
  } else if (wordLimit <= 400) {
    rcaRuleBlock = `
        Rules:
        - Respond strictly in ${selectedLanguageName || "English"}
        - Use structured format:
          1. Summary
          2. Key Issues
          3. Insights
          4. Recommendations
          5. Next Steps
        - Each section can have 1–2 sentences
        - Include basic reasoning and actionable suggestions
        - Keep response practical and structured
        - No markdown
        - No bullets outside structure
      `;
  } else {
    rcaRuleBlock = `
      Rules:
      - Respond strictly in ${selectedLanguageName || "English"}
      - Use structured format:
        1. Summary
        2. Key Issues
        3. Insights
        4. Recommendations
        5. Next Steps
      - Provide deeper analysis per section
      - Include system-level insights and improvements
      - Be detailed but structured
      - No markdown
      - No bullets outside structure
    `;
  }

  const promptText = buildPrompt({
    mode: (mode || "").toLowerCase().trim(),
    param1,
    param2,
    selectedAction,
    selectedLanguageName,
    wordLimit,
    outputFormat,
    rcaRuleBlock,
    notesType,
    audienceGender,
    audienceAge,
    audienceName
  });

  const promptLength = promptText.length;
  const totalChars = promptLength + wordLimit;

  const promptChars = typeof promptText === "string" ? promptText.length : 0;
  const inputTokens = Math.ceil(promptChars / 4);
  const outputTokens = Number(wordLimit);
  const safeOutputTokens = isNaN(outputTokens) ? 0 : outputTokens;
  // TOTAL TOKENS
  const totalTokens = inputTokens + safeOutputTokens;
  // PRICING BASE
  const USD_PER_1K_TOKENS = 0.00075;
  const USD_PER_TOKEN = USD_PER_1K_TOKENS / 1000;
  const USD_TO_INR = 95;
  const FACILITY_MULTIPLIER = 3;
  // COST CALCULATION
  const baseCostUSD = totalTokens * USD_PER_TOKEN;
  const estimatedCostINR = baseCostUSD * USD_TO_INR * FACILITY_MULTIPLIER;
  // FINAL SAFE OUTPUT (NO NaN)
  const finalCostINR = Number.isFinite(estimatedCostINR)
    ? estimatedCostINR.toFixed(4)
    : "0.0000";

  const handleGetAIResponse = async () => {
    if (!promptText.trim()) {
      setError("Please enter some content first");
      return;
    }

    setLoading(true);
    setAiResponse("");
    setError("");

    try {
      let resultText = "";
      let modelUsed = "";

      switch (selectedProvider) {
        case "openai": {
          const openai = new OpenAI({
            apiKey: OPENAI_API_KEY,
            dangerouslyAllowBrowser: true,
          });

          const completion = await openai.chat.completions.create({
            model: "gpt-4o-mini",
            messages: [{ role: "user", content: promptText }],
            max_tokens: Math.min(wordLimit, 4096),
            temperature: 0.7,
          });

          resultText = completion.choices[0]?.message?.content?.trim() || "";
          modelUsed = "gpt-4o-mini";
          break;
        }

        case "claude": {
          if (!ANTHROPIC_API_KEY) throw new Error("Anthropic API key missing");

          const anthropic = new Anthropic({ apiKey: ANTHROPIC_API_KEY });

          const msg = await anthropic.messages.create({
            model: "claude-3-5-sonnet-20241022",
            max_tokens: Math.min(wordLimit, 8192),
            temperature: 0.7,
            messages: [{ role: "user", content: promptText }],
          });

          resultText = msg.content[0].text.trim();
          modelUsed = "claude-3-5-sonnet";
          break;
        }

        case "gemini": {
          if (!GOOGLE_API_KEY) throw new Error("Google API key missing");

          const genAI = new GoogleGenerativeAI(GOOGLE_API_KEY);
          const model = genAI.getGenerativeModel({ model: "gemini-1.5-flash" });

          const result = await model.generateContent(promptText);
          resultText = result.response.text().trim();
          modelUsed = "gemini-1.5-flash";
          break;
        }

        case "grok":
          throw new Error(
            "Grok API integration not publicly available yet or requires special access",
          );

        case "mistral":
          const mistralRes = await axios.post(
            "https://api.mistral.ai/v1/chat/completions",
            {
              model: "mistral-large-latest",
              messages: [{ role: "user", content: promptText }],
              max_tokens: Math.min(wordLimit, 8192),
              temperature: 0.7,
            },
            {
              headers: {
                Authorization: "",
                //   Authorization: `Bearer ${import.meta.env.VITE_MISTRAL_API_KEY}`,
                // 'Content-Type': 'application/json',
              },
            },
          );
          resultText = mistralRes.data.choices[0].message.content.trim();
          modelUsed = "mistral-large-latest";
          break;

        case "perplexity":
          const perpRes = await axios.post(
            "https://api.perplexity.ai/chat/completions",
            {
              model: "llama-3.1-sonar-large-128k-online",
              messages: [{ role: "user", content: promptText }],
              max_tokens: Math.min(wordLimit, 4096),
            },
            {
              headers: {
                Authorization: "",
                // Authorization: `Bearer ${import.meta.env.VITE_PERPLEXITY_API_KEY}`,
                // 'Content-Type': 'application/json',
              },
            },
          );
          resultText = perpRes.data.choices[0].message.content.trim();
          modelUsed = "sonar-large-online";
          break;

        case "deepseek":
          const dsRes = await axios.post(
            "https://api.deepseek.com/v1/chat/completions",
            {
              model: "deepseek-chat",
              messages: [{ role: "user", content: promptText }],
              max_tokens: Math.min(wordLimit, 8192),
              temperature: 0.7,
            },
            {
              headers: {
                Authorization: "",
                // Authorization: `Bearer ${import.meta.env.VITE_DEEPSEEK_API_KEY}`,
              },
            },
          );
          resultText = dsRes.data.choices[0].message.content.trim();
          modelUsed = "deepseek-chat";
          break;

        case "qwen":
        case "llama":
        case "cohere":
          throw new Error(
            `${aiProviders.find((p) => p.id === selectedProvider)?.label} integration not implemented yet`,
          );

        default:
          throw new Error("Unknown provider selected");
      }

      setAiResponse(resultText);
    } catch (err) {
      console.error(err);
      const message =
        err.response?.data?.error?.message ||
        err.message ||
        "Failed to get AI response";
      setError(message);
      setAiResponse(`Error: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  const showEducationActions = (param1 || "")
    .toLowerCase()
    .includes("education");
  // console.log("promptText: ",promptText)
  return (
    <div className="flex flex-col justify-center min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-gray-100 pb-4">
      <div className="flex flex-col justify-center bg-gray-950/80 backdrop-blur-sm border-b border-indigo-500/30 py-1 px-2">
        <h1 className="text-lg lg:text-xl font-bold text-center text-yellow-200">
          Think5Whys AI Assistant
        </h1>
        {/* <p className="text-center text-gray-100 mt-1 text-sm">
          for <strong className='text-white'>{param1}</strong>
        </p> */}
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-6 space-y-6">
        <div className="bg-gray-800/60 backdrop-blur-md rounded-xl border border-gray-700/60 shadow-xl p-5 space-y-5">
          <div>
            <p className="text-cyan-200 text-xs lg:text-sm">
              Prompt Engineering
            </p>
          </div>
          <div>
            <label className="block text-xs lg:text-sm font-medium text-cyan-200 mb-1">
              Intent: What do you want to do?
            </label>
            {/* <select
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              className="h-12 w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select action...</option>

              {actionGroups.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select> */}
            {showEducationActions && (
              <div>
                <label className="block text-xs lg:text-sm font-medium text-white mb-1">
                  Education / Knowledge / Gnereal QnA Actions
                </label>

                <select
                  value={selectedAction}
                  onChange={(e) => setSelectedAction(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg text-xs lg:text-sm px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {/* <option value="">Select action...</option> */}

                  {actionGroups.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.options.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs lg:text-sm font-medium text-white mb-1">
              Output Language
            </label>
            <select
              value={selectedLanguageCode}
              onChange={(e) => setSelectedLanguageCode(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg text-xs lg:text-sm px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {/* <option value="">Select language...</option> */}
              {languageOptions.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs lg:text-sm font-medium text-white mb-1">
              Max Tokens in Response
            </label>
            <input
              type="range"
              min={100}
              max={16000}
              step={100}
              value={wordLimit}
              onChange={(e) => setWordLimit(Number(e.target.value))}
              className="w-full accent-indigo-500"
            />
            <div className="text-right text-xs lg:text-sm text-white mt-1">
              {wordLimit} chars ≈ {Math.ceil(wordLimit / 4)} tokens (est.)
            </div>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-2 text-xs lg:text-sm space-y-2 border border-gray-700">
            <p className="text-white text-xs lg:text-sm">
              Manual prompt generation with copy-paste into the form by you{" "}
              <span className="text-yellow-300 text-xs lg:text-sm font-semibold">
                ₹0
              </span>
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-4 text-sm space-y-2 border border-gray-700">
            <p className="text-white text-xs lg:text-sm">
              Estimated cost if auto-filled into your form:{" "}
              <span className="text-yellow-300 text-xs lg:text-sm font-semibold">
                ₹{estimatedCostINR.toFixed(2)}
              </span>{" "}
              ({Math.ceil(totalChars / 4)} tokens)
            </p>

            <p className="text-gray-300 text-xs lg:text-sm">
              This amount will be deducted from your Think5Whys wallet.
            </p>
          </div>

          <div>
            <label className="block text-xs lg:text-sm font-medium text-white mb-1">
              Desired Output Format
            </label>
            <select
              value={outputFormat}
              onChange={(e) => setOutputFormat(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg text-xs lg:text-sm px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {formats.map((fmt) => (
                <option key={fmt} value={fmt}>
                  {fmt}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-xs lg:text-sm font-medium text-gray-300">
            Choose AI Model / Provider
          </label>

          <select
            value={selectedProvider}
            onChange={(e) => setSelectedProvider(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg text-xs lg:text-sm px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 h-12"
          >
            {aiProviders.map((provider) => (
              <option key={provider.id} value={provider.id}>
                {provider.label}
              </option>
            ))}
          </select>
        </div>

        {/* Prompt Preview + Buttons */}
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setPromptCreated(!promptCreated)}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-medium py-1 px-2 text-xs lg:text-sm rounded-xl shadow-lg transition-all active:scale-[0.98]"
          >
            {promptCreated ? "Hide Prompt" : "Generate Prompt"}
          </button>

          {promptCreated && (
            <div className="bg-gray-900/70 border border-gray-700 rounded-xl p-2 text-xs lg:text-sm text-gray-200 whitespace-pre-wrap break-words font-mono relative">
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(promptText);
                  // Optional: Show toast / alert
                  const btn = document.getElementById("copy-btn");
                  if (btn) {
                    const originalText = btn.innerHTML;
                    btn.innerHTML = "✅ Copied!";
                    setTimeout(() => {
                      btn.innerHTML = originalText;
                    }, 2000);
                  }
                }}
                id="copy-btn"
                className="w-32 absolute top-4 right-4 flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-600 text-gray-300 hover:text-white px-2 py-1 rounded-lg text-xs lg:text-sm font-medium transition-all active:scale-95"
              >
                <i className="fa-solid fa-copy"></i>
                Copy Prompt
              </button>
              <div className="mt-2 mb-2 text-xs lg:text-sm text-yellow-100">
                {promptText}
              </div>
              <div className="mt-2 pt-2 border-t border-gray-700 text-gray-300 text-xs leading-relaxed">
                <p className="mb-1 text-gray-100 text-xs lg:text-sm font-medium">
                  Copy the prompt above — then use the button below to paste it
                  into your chosen AI.
                </p>

                <p className="mb-2 text-green-300/90 text-xs lg:text-sm leading-relaxed">
                  Good news: most of these AI providers offer{" "}
                  <strong>free daily usage</strong> — no payment needed to try!
                  <br />
                  <span className="text-gray-400 text-xs lg:text-sm">
                    (ChatGPT, Gemini, Claude, Perplexity, DeepSeek... all have
                    free tiers with generous limits)
                  </span>
                </p>

                {selectedProvider === "think5whys" ? (
                  <div className="bg-gradient-to-br from-purple-900/60 to-indigo-900/60 border border-purple-500/40 rounded-xl p-2 text-center shadow-lg">
                    <div className="text-lg lg:text-xl font-bold text-purple-300 mb-2">
                      🚀
                    </div>
                    <h3 className="text-xs lg:text-sm font-semibold text-white mb-1">
                      think5whys.ai
                    </h3>
                    <p className="text-purple-200 text-xs lg:text-sm mb-1">
                      Our native AI experience is coming soon!
                    </p>
                    <div className="inline-block px-2 py-1 bg-purple-700/50 rounded-full text-purple-100 text-xs lg:text-sm font-medium">
                      Stay tuned • 2026
                    </div>
                  </div>
                ) : (
                  <a
                    href={getProviderChatUrl(selectedProvider)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`
                      inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium text-sm
                      transition-all duration-200 hover:brightness-110 active:scale-[0.97] shadow-md
                      bg-gradient-to-r ${aiProviders.find((p) => p.id === selectedProvider)?.color || "from-gray-600 to-gray-700"}
                      text-white
                    `}
                  >
                    <span className="text-xs lg:text-sm">
                      Go to{" "}
                      {aiProviders.find((p) => p.id === selectedProvider)
                        ?.shortName || "AI"}
                    </span>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                )}

                <p className="mt-2 text-gray-400 text-xs lg:text-sm text-center">
                  After getting the result, copy it and paste wherever you need
                  it on the Think5Whys platform.
                </p>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={handleGetAIResponse}
            disabled={loading || !selectedAction || !selectedLanguageCode}
            className={`
              w-full py-1 px-2 rounded-xl font-bold text-xs lg:text-sm shadow-xl transition-all
              ${
                loading
                  ? "bg-gray-600 cursor-not-allowed"
                  : `bg-gradient-to-r ${aiProviders.find((p) => p.id === selectedProvider)?.color || "from-emerald-600 to-teal-600"} hover:brightness-110 active:scale-[0.98]`
              }
            `}
          >
            {loading ? (
              <span className="flex items-center text-xs lg:text-sm justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                </svg>
                Processing...
              </span>
            ) : (
              `Get Response from ${aiProviders.find((p) => p.id === selectedProvider)?.shortName || "AI"} →`
            )}
          </button>

          {error && (
            <div className="bg-red-900/40 border border-red-600/50 text-red-200 px-2 py-1 rounded-lg text-xs lg:text-sm">
              {error}
            </div>
          )}

          {aiResponse && (
            <div className="bg-gray-900/80 border border-indigo-500/30 rounded-xl p-2 text-xs lg:text-sm whitespace-pre-wrap text-gray-100 leading-relaxed">
              {aiResponse}
            </div>
          )}
        </div>

        <p className="text-center text-[10px] lg:text-xs text-yellow-100 mt-4">
          Think5Whys is not responsible for AI-generated content.
          <br />
          All charges are final and non-refundable.
        </p>
      </div>
    </div>
  );
};

export default AILightComponent;
